import React, { useState, useMemo } from 'react';
import { 
  Star, Trophy, Sparkles, Award, X, Rocket 
} from 'lucide-react';

// ✅ CONEXIÓN AL SISTEMA GLOBAL (Ruta Absoluta)
import { useGlobal } from 'context/GlobalContext';

const BitacoraConducta = () => {
  // 1. CONSUMO DE DATOS REALES DESDE EL CEREBRO DE LA APP
  const { ninos, setNinos, crearAlerta } = useGlobal();
  
  const [ninoSeleccionado, setNinoSeleccionado] = useState(null);
  const [mostrarLogros, setMostrarLogros] = useState(false);

  // CONFIGURACIÓN DEL GRUPO (En la v3.1 esto se detectará por el perfil del maestro)
  const grupoNombre = 'PÁRVULOS B';

  // FILTRAR NIÑOS: Solo los que están presentes en este salón hoy para evitar errores
  const alumnosPresentes = useMemo(() => {
    return ninos.filter(n => n.salon === grupoNombre && n.estatus === 'en_salon');
  }, [ninos]);

  const acciones = [
    { id: 'super', label: '¡Súper!', puntos: 5, emoji: '🚀', color: 'bg-emerald-500' },
    { id: 'amigo', label: 'Gran Amigo', puntos: 3, emoji: '🤝', color: 'bg-blue-500' },
    { id: 'atento', label: 'Atento', puntos: 2, emoji: '💡', color: 'bg-amber-500' },
    { id: 'aviso', label: 'Inquieto', puntos: -2, emoji: '🤫', color: 'bg-orange-500' },
    { id: 'alerta', label: 'Reporte', puntos: 0, emoji: '⚠️', color: 'bg-rose-600' },
  ];

  const catalogoLogros = [
    { emoji: '🦖', nombre: 'T-Rex Pro' }, { emoji: '🏎️', nombre: 'Veloz' },
    { emoji: '🦄', nombre: 'Mágico' }, { emoji: '🦁', nombre: 'Valiente' },
    { emoji: '🚀', nombre: 'Astronauta' }, { emoji: '🎨', nombre: 'Artista' }
  ];

  // --- LÓGICA DE ACTUALIZACIÓN GLOBAL ---
  
  const recompensarATodos = () => {
    if(window.confirm("¿Premiar a TODA la clase con +5 estrellas?")) {
      setNinos(prev => prev.map(n => 
        (n.salon === grupoNombre && n.estatus === 'en_salon') 
          ? { ...n, estrellas: (n.estrellas || 0) + 5 } 
          : n
      ));
      alert("¡MODO FIESTA! ✨ Estrellas enviadas a todos los presentes.");
    }
  };

  const aplicarAccion = (accion) => {
    if (accion.id === 'alerta') {
      // Dispara incidencia al Admin y registra hora
      crearAlerta(ninoSeleccionado.nombre, 'CONDUCTA', `Reporte de comportamiento inquieto en salón.`);
      alert(`⚠️ Alerta enviada al Admin para ${ninoSeleccionado.nombre}`);
    } else {
      // Actualiza estrellas en el estado global para que el padre lo vea en tiempo real
      setNinos(prev => prev.map(n => 
        n.id === ninoSeleccionado.id 
          ? { ...n, estrellas: (n.estrellas || 0) + accion.puntos } 
          : n
      ));
    }
    setNinoSeleccionado(null);
  };

  const asignarLogro = (logroEmoji) => {
    setNinos(prev => prev.map(n => 
      n.id === ninoSeleccionado.id ? { ...n, logro: logroEmoji } : n
    ));
    alert(`¡Logro asignado! ${ninoSeleccionado.nombre} tiene su ${logroEmoji}`);
    setMostrarLogros(false);
    setNinoSeleccionado(null);
  };

  return (
    <div className="w-full max-w-[1400px] mx-auto space-y-8 animate-in fade-in duration-500 pb-32 px-4 text-slate-900 text-left font-sans">
      
      {/* HEADER: HONOR ROLL */}
      <div className="bg-[#0A2440] rounded-[3.5rem] p-8 md:p-12 text-white flex flex-col xl:flex-row justify-between items-center gap-8 shadow-2xl relative overflow-hidden">
        <div className="flex items-center gap-6">
          <div className="w-20 h-20 bg-indigo-600 rounded-[2rem] flex items-center justify-center border-4 border-indigo-400 shadow-lg shrink-0">
            <Trophy size={44} className="text-amber-400" />
          </div>
          <div className="text-left">
            <h2 className="text-4xl md:text-5xl font-black italic tracking-tighter uppercase leading-none">Honor Roll</h2>
            <p className="text-indigo-400 font-black text-[10px] uppercase tracking-[0.4em] mt-2 italic">{grupoNombre}</p>
          </div>
        </div>
        
        <button 
          onClick={recompensarATodos}
          className="bg-emerald-600 hover:bg-emerald-500 text-white px-10 py-6 rounded-[2.5rem] font-black uppercase text-sm tracking-widest flex items-center gap-4 shadow-xl transition-all active:scale-95 border-b-8 border-emerald-800"
        >
          <Sparkles size={24} /> ¡Recompensa Grupal! (+5)
        </button>
      </div>

      {/* GRID DE ALUMNOS PRESENTES */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
        {alumnosPresentes.length === 0 ? (
          <div className="col-span-full py-20 text-center bg-slate-50 rounded-[3rem] border-4 border-dashed border-slate-200 opacity-40 uppercase font-black italic">
            Esperando ingresos en este salón...
          </div>
        ) : (
          alumnosPresentes.map((nino) => (
            <button
              key={nino.id}
              onClick={() => setNinoSeleccionado(nino)}
              className={`relative p-8 rounded-[3.5rem] border-4 transition-all flex flex-col items-center gap-3 shadow-sm ${
                ninoSeleccionado?.id === nino.id ? 'bg-indigo-50 border-indigo-500 scale-105 shadow-xl' : 'bg-white border-slate-100'
              }`}
            >
              {/* LOGRO VISUAL DINÁMICO */}
              {nino.logro && (
                <div className="absolute -top-2 -right-2 bg-white w-12 h-12 rounded-full shadow-lg border-4 border-amber-400 flex items-center justify-center text-2xl animate-bounce z-10">
                  {nino.logro}
                </div>
              )}

              <div className="text-6xl my-4 drop-shadow-md">{nino.emoji || '😊'}</div>
              
              <div className="text-center">
                <h4 className="font-black text-lg leading-none mb-2 uppercase tracking-tight italic">{nino.nombre.split(' ')[0]}</h4>
                <div className="flex items-center justify-center gap-2 bg-amber-100 text-amber-700 px-4 py-1 rounded-full text-xs font-black">
                  <Star size={12} fill="currentColor" /> {nino.estrellas || 0}
                </div>
              </div>
            </button>
          ))
        )}
      </div>

      {/* MODAL DE ACCIÓN MAESTRO */}
      {ninoSeleccionado && (
        <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-md z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white w-full max-w-2xl rounded-t-[4rem] sm:rounded-[4rem] p-8 md:p-12 space-y-8 animate-in slide-in-from-bottom-full duration-400 max-h-[90vh] overflow-y-auto hide-scrollbar border-t-8 border-indigo-600">
            
            <div className="flex justify-between items-center sticky top-0 bg-white pb-4 z-10">
              <div className="flex items-center gap-4 text-left">
                <span className="text-6xl">{ninoSeleccionado.emoji || '😊'}</span>
                <h3 className="text-3xl font-black uppercase italic tracking-tighter leading-none">{ninoSeleccionado.nombre}</h3>
              </div>
              <button onClick={() => {setNinoSeleccionado(null); setMostrarLogros(false);}} className="p-3 bg-slate-100 rounded-full text-slate-400 hover:text-rose-600 transition-colors"><X size={32}/></button>
            </div>

            {!mostrarLogros ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {acciones.map((acc) => (
                  <button
                    key={acc.id}
                    onClick={() => aplicarAccion(acc)}
                    className={`${acc.color} p-6 rounded-[2.5rem] flex flex-col items-center gap-2 text-white shadow-xl hover:brightness-110 active:scale-95 transition-all`}
                  >
                    <span className="text-4xl">{acc.emoji}</span>
                    <span className="text-[10px] font-black uppercase tracking-widest italic">{acc.label}</span>
                    <span className="text-xl font-black bg-white/20 px-4 py-1 rounded-lg">
                      {acc.puntos > 0 ? `+${acc.puntos}` : acc.puntos === 0 ? 'SOS' : acc.puntos}
                    </span>
                  </button>
                ))}
                
                <button 
                  onClick={() => setMostrarLogros(true)}
                  className="bg-slate-900 p-6 rounded-[2.5rem] flex flex-col items-center gap-2 text-amber-400 shadow-xl border-4 border-amber-400/30"
                >
                  <Award size={40} />
                  <span className="text-[10px] font-black uppercase tracking-widest text-white italic">Logro Especial</span>
                  <span className="text-[9px] font-bold text-slate-400 uppercase">Honor</span>
                </button>
              </div>
            ) : (
              <div className="space-y-6 animate-in zoom-in-95 text-left">
                <div className="flex items-center gap-3 text-indigo-600">
                  <Award size={32} />
                  <h4 className="text-2xl font-black uppercase italic tracking-tighter">Asignar Logro de Honor</h4>
                </div>
                <div className="grid grid-cols-3 gap-4 pb-4">
                  {catalogoLogros.map((logro) => (
                    <button
                      key={logro.emoji}
                      onClick={() => asignarLogro(logro.emoji)}
                      className="bg-slate-50 border-4 border-slate-100 p-6 rounded-[2.5rem] flex flex-col items-center gap-2 hover:border-indigo-500 transition-all group"
                    >
                      <span className="text-5xl group-hover:scale-110 transition-transform">{logro.emoji}</span>
                      <span className="text-[9px] font-black uppercase tracking-tighter text-slate-400 italic">{logro.nombre}</span>
                    </button>
                  ))}
                </div>
                <button onClick={() => setMostrarLogros(false)} className="w-full text-center text-slate-400 font-black uppercase text-xs tracking-widest">Volver al Tablero</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BitacoraConducta;